import type { Rng } from './rng';
import { idFor } from './ids';
import { SOURCE_SYSTEMS, provenanceFactory } from './provenance';
import type { Department, Enterprise, ProductGroup, ValueStream } from './types';
import { millions, pad } from './support';

/**
 * Aurora Airlines' organisation.
 *
 * Note the ragged shape: twelve product groups sit under five of the fourteen
 * value streams, and the remaining nine streams own products directly. That is
 * deliberate. Real carriers do not have a uniform four-level tree, and a
 * hierarchy component that has only ever been fed a perfect lattice breaks the
 * first time it meets one that is not.
 */

export const TENANT_ID = 'aurora-airlines';
export const ENTERPRISE_CODE = 'ENT-AURORA';

interface DepartmentSeed {
  code: string;
  name: string;
  ordinal: number;
  description: string;
  leaderPersonCode: string;
}

const DEPARTMENT_SEEDS: readonly DepartmentSeed[] = [
  {
    code: 'DEP-NOPS',
    name: 'Network & Operations',
    ordinal: 1,
    description:
      'Flight, crew, airport and maintenance operations technology — the systems that move aircraft and people on the day.',
    leaderPersonCode: 'PPL-0015',
  },
  {
    code: 'DEP-CX',
    name: 'Customer Experience',
    ordinal: 2,
    description:
      'Shopping, booking, servicing, care and loyalty across web, mobile, kiosk and contact centre.',
    leaderPersonCode: 'PPL-0016',
  },
  {
    code: 'DEP-COMM',
    name: 'Commercial',
    ordinal: 3,
    description: 'Pricing, revenue management, ancillary retailing and cargo.',
    leaderPersonCode: 'PPL-0017',
  },
  {
    code: 'DEP-CORP',
    name: 'Corporate & Shared Services',
    ordinal: 4,
    description:
      'Finance, revenue accounting, payments, people systems and the shared platforms behind them.',
    leaderPersonCode: 'PPL-0018',
  },
  {
    code: 'DEP-DATA',
    name: 'Data, Analytics & AI',
    ordinal: 5,
    description: 'The data platform, analytics products and applied AI capability.',
    leaderPersonCode: 'PPL-0019',
  },
];

interface ValueStreamSeed {
  code: string;
  name: string;
  ordinal: string;
  departmentCode: string;
  description: string;
  ownerPersonCode: string;
  /** Annual business value in $m, matching the scale on the reference screens. */
  businessValueMillions: number;
}

const VALUE_STREAM_SEEDS: readonly ValueStreamSeed[] = [
  {
    code: 'VS-FLTOPS',
    name: 'Flight Operations',
    ordinal: '1.1',
    departmentCode: 'DEP-NOPS',
    description: 'Dispatch, flight planning, weight and balance, and day-of-operations control.',
    ownerPersonCode: 'PPL-0021',
    businessValueMillions: 428.6,
  },
  {
    code: 'VS-CREW',
    name: 'Crew Operations',
    ordinal: '1.2',
    departmentCode: 'DEP-NOPS',
    description: 'Crew planning, rostering, tracking and disruption recovery.',
    ownerPersonCode: 'PPL-0020',
    businessValueMillions: 268.4,
  },
  {
    code: 'VS-ARPT',
    name: 'Airport Journey',
    ordinal: '1.3',
    departmentCode: 'DEP-NOPS',
    description: 'Check-in, boarding, baggage and the aircraft turn.',
    ownerPersonCode: 'PPL-0022',
    businessValueMillions: 162.7,
  },
  {
    code: 'VS-MTCE',
    name: 'Maintenance & Engineering',
    ordinal: '1.4',
    departmentCode: 'DEP-NOPS',
    description: 'Line and base maintenance planning, technical records and reliability.',
    ownerPersonCode: 'PPL-0023',
    businessValueMillions: 96.4,
  },
  {
    code: 'VS-NETPLAN',
    name: 'Network Planning',
    ordinal: '1.5',
    departmentCode: 'DEP-NOPS',
    description: 'Schedule design, slot management and fleet assignment.',
    ownerPersonCode: 'PPL-0024',
    businessValueMillions: 88.2,
  },
  {
    code: 'VS-BOOK',
    name: 'Book & Manage Trip',
    ordinal: '2.1',
    departmentCode: 'DEP-CX',
    description: 'Shopping, offer, order, payment and post-booking servicing.',
    ownerPersonCode: 'PPL-0025',
    businessValueMillions: 312.4,
  },
  {
    code: 'VS-CARE',
    name: 'Customer Care & Notifications',
    ordinal: '2.2',
    departmentCode: 'DEP-CX',
    description: 'Contact centre, self-service care and proactive customer messaging.',
    ownerPersonCode: 'PPL-0026',
    businessValueMillions: 74.9,
  },
  {
    code: 'VS-LOYALTY',
    name: 'Loyalty & Partnerships',
    ordinal: '2.3',
    departmentCode: 'DEP-CX',
    description: 'Frequent flyer accrual and redemption, partner and co-brand settlement.',
    ownerPersonCode: 'PPL-0027',
    businessValueMillions: 186.5,
  },
  {
    code: 'VS-PRM',
    name: 'Pricing & Revenue Management',
    ordinal: '3.1',
    departmentCode: 'DEP-COMM',
    description: 'Fare construction, inventory control and revenue optimisation.',
    ownerPersonCode: 'PPL-0028',
    businessValueMillions: 268.1,
  },
  {
    code: 'VS-RETAIL',
    name: 'Ancillary & Retailing',
    ordinal: '3.2',
    departmentCode: 'DEP-COMM',
    description: 'Seats, bags, upgrades, bundles and third-party retailing.',
    ownerPersonCode: 'PPL-0029',
    businessValueMillions: 141.8,
  },
  {
    code: 'VS-CARGO',
    name: 'Cargo',
    ordinal: '3.3',
    departmentCode: 'DEP-COMM',
    description: 'Cargo booking, capacity, handling and settlement.',
    ownerPersonCode: 'PPL-0030',
    businessValueMillions: 93.5,
  },
  {
    code: 'VS-FIN',
    name: 'Finance & Revenue Accounting',
    ordinal: '4.1',
    departmentCode: 'DEP-CORP',
    description: 'Revenue accounting, proration, interline settlement, payments and billing.',
    ownerPersonCode: 'PPL-0031',
    businessValueMillions: 118.7,
  },
  {
    code: 'VS-PEOPLE',
    name: 'People & Workplace',
    ordinal: '4.2',
    departmentCode: 'DEP-CORP',
    description: 'HR, learning, workplace and corporate productivity systems.',
    ownerPersonCode: 'PPL-0032',
    businessValueMillions: 42.3,
  },
  {
    code: 'VS-DATAAI',
    name: 'Data & AI Platform',
    ordinal: '5.1',
    departmentCode: 'DEP-DATA',
    description: 'The data hub, analytics products, ML platform and applied AI use cases.',
    ownerPersonCode: 'PPL-0033',
    businessValueMillions: 97.6,
  },
];

interface ProductGroupSeed {
  code: string;
  name: string;
  ordinal: string;
  valueStreamCode: string;
  ownerPersonCode: string;
}

const PRODUCT_GROUP_SEEDS: readonly ProductGroupSeed[] = [
  {
    code: 'PG-DISPATCH',
    name: 'Flight Dispatch & Planning',
    ordinal: '1.1.1',
    valueStreamCode: 'VS-FLTOPS',
    ownerPersonCode: 'PPL-0039',
  },
  {
    code: 'PG-FUEL',
    name: 'Fuel & Efficiency',
    ordinal: '1.1.2',
    valueStreamCode: 'VS-FLTOPS',
    ownerPersonCode: 'PPL-0040',
  },
  {
    code: 'PG-CREWPLAN',
    name: 'Crew Planning',
    ordinal: '1.2.1',
    valueStreamCode: 'VS-CREW',
    ownerPersonCode: 'PPL-0034',
  },
  {
    code: 'PG-CREWOPS',
    name: 'Crew Day-of-Operations',
    ordinal: '1.2.2',
    valueStreamCode: 'VS-CREW',
    ownerPersonCode: 'PPL-0035',
  },
  {
    code: 'PG-PAX',
    name: 'Passenger Processing',
    ordinal: '1.3.1',
    valueStreamCode: 'VS-ARPT',
    ownerPersonCode: 'PPL-0038',
  },
  {
    code: 'PG-BAG',
    name: 'Baggage',
    ordinal: '1.3.2',
    valueStreamCode: 'VS-ARPT',
    ownerPersonCode: 'PPL-0037',
  },
  {
    code: 'PG-TURN',
    name: 'Airport Turn',
    ordinal: '1.3.3',
    valueStreamCode: 'VS-ARPT',
    ownerPersonCode: 'PPL-0036',
  },
  {
    code: 'PG-SHOP',
    name: 'Shopping & Booking',
    ordinal: '2.1.1',
    valueStreamCode: 'VS-BOOK',
    ownerPersonCode: 'PPL-0041',
  },
  {
    code: 'PG-TRIP',
    name: 'Trip Servicing',
    ordinal: '2.1.2',
    valueStreamCode: 'VS-BOOK',
    ownerPersonCode: 'PPL-0042',
  },
  {
    code: 'PG-LOYCORE',
    name: 'Loyalty Core',
    ordinal: '2.3.1',
    valueStreamCode: 'VS-LOYALTY',
    ownerPersonCode: 'PPL-0043',
  },
  {
    code: 'PG-REVACC',
    name: 'Revenue Accounting',
    ordinal: '4.1.1',
    valueStreamCode: 'VS-FIN',
    ownerPersonCode: 'PPL-0044',
  },
  {
    code: 'PG-PAY',
    name: 'Payments',
    ordinal: '4.1.2',
    valueStreamCode: 'VS-FIN',
    ownerPersonCode: 'PPL-0045',
  },
];

export interface OrganizationData {
  enterprise: Enterprise;
  departments: Department[];
  valueStreams: ValueStream[];
  productGroups: ProductGroup[];
}

export function buildOrganization(asOf: string, rng: Rng): OrganizationData {
  const stamp = provenanceFactory(asOf, rng.fork('organization'));

  const enterprise: Enterprise = {
    id: idFor('Enterprise', ENTERPRISE_CODE),
    businessCode: ENTERPRISE_CODE,
    name: 'Aurora Airlines',
    tenantId: TENANT_ID,
    provenance: stamp(SOURCE_SYSTEMS.hcm, 'HCM-ORG-0001', {
      meanAgeDays: 21,
      meanConfidence: 0.97,
    }),
  };

  const departments = DEPARTMENT_SEEDS.map((seed, index) => ({
    id: idFor('OrganizationalUnit', seed.code),
    businessCode: seed.code,
    name: seed.name,
    ordinal: seed.ordinal,
    enterpriseId: enterprise.id,
    description: seed.description,
    leaderPersonCode: seed.leaderPersonCode,
    provenance: stamp(SOURCE_SYSTEMS.hcm, `HCM-ORG-${pad(index + 2, 4)}`, {
      meanAgeDays: 18,
      meanConfidence: 0.95,
    }),
  }));

  const valueStreams = VALUE_STREAM_SEEDS.map((seed, index) => ({
    id: idFor('ValueStream', seed.code),
    businessCode: seed.code,
    name: seed.name,
    ordinal: seed.ordinal,
    departmentCode: seed.departmentCode,
    description: seed.description,
    ownerPersonCode: seed.ownerPersonCode,
    annualBusinessValueCents: millions(seed.businessValueMillions),
    provenance: stamp(SOURCE_SYSTEMS.productRegistry, `PR-VS-${pad(index + 1, 4)}`, {
      meanAgeDays: 11,
      meanConfidence: 0.9,
    }),
  }));

  const productGroups = PRODUCT_GROUP_SEEDS.map((seed, index) => ({
    id: idFor('ProductGroup', seed.code),
    businessCode: seed.code,
    name: seed.name,
    ordinal: seed.ordinal,
    valueStreamCode: seed.valueStreamCode,
    ownerPersonCode: seed.ownerPersonCode,
    provenance: stamp(SOURCE_SYSTEMS.productRegistry, `PR-PG-${pad(index + 1, 4)}`, {
      meanAgeDays: 13,
      meanConfidence: 0.88,
    }),
  }));

  return { enterprise, departments, valueStreams, productGroups };
}
